import { useEffect, useState } from "react";
import { updateProject } from "../services/projectApi";
import {
  X,
  Building2,
  Calendar,
  User,
  Clock,
  DollarSign,
  FileText,
} from "lucide-react";

const statusStyles = {
  Active: "bg-green-100 text-green-700",
  Planning: "bg-blue-100 text-blue-700",
  Completed: "bg-emerald-100 text-emerald-700",
  "On Hold": "bg-yellow-100 text-yellow-700",
  Cancelled: "bg-red-100 text-red-700",
  Draft: "bg-slate-100 text-slate-700",
};

const priorityStyles = {
  Low: "bg-gray-100 text-gray-700",
  Medium: "bg-blue-100 text-blue-700",
  High: "bg-orange-100 text-orange-700",
  Critical: "bg-red-100 text-red-700",
};

const inputClass =
  "w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

const disabledInputClass =
  "w-full rounded-lg border border-slate-200 bg-slate-100 px-4 py-3 text-sm text-slate-500 outline-none";

const labelClass =
  "mb-2 block text-sm font-semibold text-slate-700";

export default function EditProjectDrawer({
  open,
  project,
  onClose,
}) {
const [formData, setFormData] = useState({});
const [saving, setSaving] = useState(false);
const [error, setError] = useState("");
  useEffect(() => {
  if (project) {
    setFormData({
      project_name:
        project.projectName ||
        project.project_name ||
        "",

      project_code:
        project.projectCode ||
        project.project_code ||
        "",

      project_type:
        project.projectType ||
        project.project_type ||
        "",

      execution_mode:
        project.executionMode ||
        project.execution_mode ||
        "",

      priority:
        project.priority ||
        "",

      status:
        project.status ||
        "",

      planned_start_date:
        project.plannedStartDate ||
        project.planned_start_date ||
        project.startDate ||
        "",

      planned_end_date:
        project.plannedEndDate ||
        project.planned_end_date ||
        project.endDate ||
        "",

      actual_start_date:
        project.actualStartDate ||
        project.actual_start_date ||
        "",

      actual_end_date:
        project.actualEndDate ||
        project.actual_end_date ||
        "",

      project_manager_employee_uuid:
        project.project_manager_employee_uuid ||
        "",

      project_manager_name:
        project.project_manager_name ||
        project.projectManagerName ||
        "",

      estimated_hours:
        project.estimatedHours ||
        project.estimated_hours ||
        "",

      estimated_cost:
        project.estimatedCost ||
        project.estimated_cost ||
        "",

      client_name:
        project.clientName ||
        project.client_name ||
        "",

      description:
        project.description ||
        "",

      // API fields
      organization_uuid:
        project.organization_uuid ||
        "",

      created_by_employee_uuid:
        project.created_by_employee_uuid ||
        "",
    });
  }
}, [project]);

  if (!open || !project) {
    return null;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

const handleSave = async () => {
  try {
    setSaving(true);
    setError("");

    // Your project object contains `uuid`
    const projectUuid = project?.uuid || project?.project_uuid;

    if (!projectUuid) {
      throw new Error("Project UUID is missing");
    }

    const payload = {
      organization_uuid: project.organization_uuid || "",

      project_name: formData.project_name,

      project_manager_employee_uuid:
        formData.project_manager_employee_uuid || null,

      project_type: formData.project_type,

      execution_mode: formData.execution_mode,

      priority: formData.priority,

      planned_start_date:
        formData.planned_start_date || null,

      planned_end_date:
        formData.planned_end_date || null,

      actual_start_date:
        formData.actual_start_date || null,

      actual_end_date:
        formData.actual_end_date || null,

      estimated_hours:
        formData.estimated_hours
          ? Number(formData.estimated_hours)
          : null,

      estimated_cost:
        formData.estimated_cost
          ? Number(formData.estimated_cost)
          : null,

      client_name:
        formData.client_name || null,

      description:
        formData.description || null,

      status: formData.status,

      created_by_employee_uuid:
        project.created_by_employee_uuid || null,
    };

    console.log("Project UUID:", projectUuid);
    console.log("Sending update:", payload);

    const response = await updateProject(
      projectUuid,
      payload
    );

    console.log("Project updated successfully:", response);

    onClose();

  } catch (error) {
    console.error("Update project error:", error);

    setError(
      error.message || "Failed to update project"
    );

  } finally {
    setSaving(false);
  }
};
  const formatDateForInput = (date) => {
    if (!date) return "";

    return date.substring(0, 10);
  };

  return (
    <>
      {/* Background Overlay */}

      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
      />

      {/* Drawer */}
      
<div className="fixed right-0 top-0 z-50 flex h-screen w-full sm:w-1/2 flex-col bg-white shadow-2xl">

        {/* HEADER */}

        <div className="flex items-start justify-between border-b bg-white px-7 py-5">

          <div>
            <h2 className="text-2xl font-bold text-slate-900">
              {formData.project_name}
            </h2>

            <p className="mt-1 text-sm text-blue-600">
              {formData.project_code}
            </p>
          </div>

          <div className="flex items-center gap-3">

            {/* Status */}

            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                statusStyles[formData.status] ||
                "bg-gray-100 text-gray-700"
              }`}
            >
              {formData.status}
            </span>

            {/* Priority */}

            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                priorityStyles[formData.priority] ||
                "bg-gray-100 text-gray-700"
              }`}
            >
              {formData.priority}
            </span>

            {/* Close */}

            <button
              type="button"
              onClick={onClose}
              className="ml-2 rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            >
              <X size={22} />
            </button>

          </div>

        </div>

        {/* BODY */}

        <div className="flex-1 overflow-y-auto bg-slate-50 px-7 py-6">

          {/* ============================= */}
          {/* BASIC INFORMATION */}
          {/* ============================= */}
          {error && (
  <div className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
    {error}
  </div>
)}

          <section className="mb-8">

            <div className="mb-5 flex items-center gap-3">

              <div className="rounded-lg bg-blue-100 p-2">
                <Building2
                  size={20}
                  className="text-blue-600"
                />
              </div>

              <h3 className="text-base font-bold uppercase tracking-wide text-slate-700">
                Basic Information
              </h3>

            </div>

            <div className="grid grid-cols-2 gap-5">

              {/* Project Name */}

              <div className="col-span-2">

                <label className={labelClass}>
                  Project Name
                </label>

                <input
                  type="text"
                  name="project_name"
                  value={formData.project_name || ""}
                  onChange={handleChange}
                  className={inputClass}
                />

              </div>

              {/* Project Code */}

              <div>

                <label className={labelClass}>
                  Project Code
                </label>

                <input
                  type="text"
                  value={formData.project_code || ""}
                  disabled
                  className={disabledInputClass}
                />

              </div>

              {/* Project Type */}

              <div>

                <label className={labelClass}>
                  Project Type
                </label>

                <input
                  type="text"
                  name="project_type"
                  value={formData.project_type || ""}
                  onChange={handleChange}
                  className={inputClass}
                />

              </div>

              {/* Execution Mode */}

              <div>

                <label className={labelClass}>
                  Execution Mode
                </label>

                <select
                  name="execution_mode"
                  value={formData.execution_mode || ""}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="Work">Work</option>
                  <option value="Pipeline">Pipeline</option>
                  <option value="Hybrid">Hybrid</option>
                </select>

              </div>

              {/* Priority */}

              <div>

                <label className={labelClass}>
                  Priority
                </label>

                <select
                  name="priority"
                  value={formData.priority || ""}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                  <option value="Critical">Critical</option>
                </select>

              </div>

              {/* Status */}

              <div>

                <label className={labelClass}>
                  Status
                </label>

                <select
                  name="status"
                  value={formData.status || ""}
                  onChange={handleChange}
                  className={inputClass}
                >
                  <option value="Draft">Draft</option>
                  <option value="Planning">Planning</option>
                  <option value="Active">Active</option>
                  <option value="On Hold">On Hold</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>

              </div>

              {/* Project Manager */}

              <div>

                <label className={labelClass}>
                  Project Manager
                </label>

                <div className="relative">

                  <User
                    size={18}
                    className="absolute left-3 top-3.5 text-slate-400"
                  />

                  <input
                    type="text"
                    value={
                      formData.project_manager_name ||
                      "Project Manager"
                    }
                    readOnly
                    className="w-full rounded-lg border border-slate-300 bg-slate-100 py-3 pl-10 pr-4 text-sm text-slate-700"
                  />

                </div>

              </div>

            </div>

          </section>

          {/* ============================= */}
          {/* PROJECT SCHEDULE */}
          {/* ============================= */}

          <section className="mb-8">

            <div className="mb-5 flex items-center gap-3">

              <div className="rounded-lg bg-blue-100 p-2">
                <Calendar
                  size={20}
                  className="text-blue-600"
                />
              </div>

              <h3 className="text-base font-bold uppercase tracking-wide text-slate-700">
                Project Schedule
              </h3>

            </div>

            <div className="grid grid-cols-2 gap-5">

              {/* Planned Start */}

              <div>

                <label className={labelClass}>
                  Planned Start Date
                </label>

                <input
                  type="date"
                  name="planned_start_date"
                  value={formatDateForInput(
                    formData.planned_start_date
                  )}
                  onChange={handleChange}
                  className={inputClass}
                />

              </div>

              {/* Planned End */}

              <div>

                <label className={labelClass}>
                  Planned End Date
                </label>

                <input
                  type="date"
                  name="planned_end_date"
                  value={formatDateForInput(
                    formData.planned_end_date
                  )}
                  onChange={handleChange}
                  className={inputClass}
                />

              </div>

              {/* Actual Start */}

              <div>

                <label className={labelClass}>
                  Actual Start Date
                </label>

                <input
                  type="date"
                  name="actual_start_date"
                  value={formatDateForInput(
                    formData.actual_start_date
                  )}
                  onChange={handleChange}
                  className={inputClass}
                />

              </div>

              {/* Actual End */}

              <div>

                <label className={labelClass}>
                  Actual End Date
                </label>

                <input
                  type="date"
                  name="actual_end_date"
                  value={formatDateForInput(
                    formData.actual_end_date
                  )}
                  onChange={handleChange}
                  className={inputClass}
                />

              </div>

            </div>

          </section>

          {/* ============================= */}
          {/* PROJECT ESTIMATION */}
          {/* ============================= */}

          <section className="mb-8">

            <div className="mb-5 flex items-center gap-3">

              <div className="rounded-lg bg-blue-100 p-2">
                <Clock
                  size={20}
                  className="text-blue-600"
                />
              </div>

              <h3 className="text-base font-bold uppercase tracking-wide text-slate-700">
                Project Estimation
              </h3>

            </div>

            <div className="grid grid-cols-2 gap-5">

              {/* Estimated Hours */}

              <div>

                <label className={labelClass}>
                  Estimated Hours
                </label>

                <input
                  type="number"
                  name="estimated_hours"
                  value={formData.estimated_hours || ""}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="250"
                />

              </div>

              {/* Estimated Cost */}

              <div>

                <label className={labelClass}>
                  Estimated Cost
                </label>

                <div className="relative">

                  <DollarSign
                    size={17}
                    className="absolute left-3 top-3.5 text-slate-400"
                  />

                  <input
                    type="number"
                    name="estimated_cost"
                    value={formData.estimated_cost || ""}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="120000"
                  />

                </div>

              </div>

            </div>

          </section>

          {/* ============================= */}
          {/* CLIENT INFORMATION */}
          {/* ============================= */}

          <section className="mb-8">

            <div className="mb-5 flex items-center gap-3">

              <div className="rounded-lg bg-blue-100 p-2">
                <Building2
                  size={20}
                  className="text-blue-600"
                />
              </div>

              <h3 className="text-base font-bold uppercase tracking-wide text-slate-700">
                Client Information
              </h3>

            </div>

            <div>

              <label className={labelClass}>
                Client Name
              </label>

              <input
                type="text"
                name="client_name"
                value={formData.client_name || ""}
                onChange={handleChange}
                className={inputClass}
                placeholder="Client name"
              />

            </div>

          </section>

          {/* ============================= */}
          {/* DESCRIPTION */}
          {/* ============================= */}

          <section>

            <div className="mb-5 flex items-center gap-3">

              <div className="rounded-lg bg-blue-100 p-2">
                <FileText
                  size={20}
                  className="text-blue-600"
                />
              </div>

              <h3 className="text-base font-bold uppercase tracking-wide text-slate-700">
                Description
              </h3>

            </div>

            <textarea
              name="description"
              value={formData.description || ""}
              onChange={handleChange}
              rows={5}
              className={inputClass}
              placeholder="Enter project description..."
            />

          </section>

        </div>

        {/* FOOTER */}

        <div className="flex items-center justify-end gap-3 border-t bg-white px-7 py-4">

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-6 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Cancel
          </button>

          <button
  type="button"
  onClick={handleSave}
  disabled={saving}
  className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
>
  {saving ? "Saving..." : "Save Changes"}
</button>

        </div>

      </div>
    </>
  );
}